import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Sparkles, Loader2, TrendingDown, Star, Banknote, ClipboardList, FolderOpen } from 'lucide-react';

/**
 * Cross-module widgets aggregating signals from suppliers, menu engineering,
 * reconciliation, and uploaded files. Plus an AI-generated unified insights block.
 */
export const CrossModuleWidgets = ({ clientId, API }) => {
  const [data, setData] = useState(null);
  const [unified, setUnified] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unifiedLoading, setUnifiedLoading] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    axios.get(`${API}/dashboard/widgets/${clientId}`)
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [clientId, API]);

  const loadUnified = async (refresh = false) => {
    setUnifiedLoading(true);
    try {
      const url = refresh
        ? `${API}/dashboard/unified-insights/${clientId}?refresh=true`
        : `${API}/dashboard/unified-insights/${clientId}`;
      const res = await axios.get(url);
      setUnified(res.data);
    } catch (e) {
      setUnified({ ai_insights: 'Failed to generate insights.' });
    } finally {
      setUnifiedLoading(false);
    }
  };

  if (loading) return (
    <div className="bg-white border border-border rounded-md p-6 text-center text-text-muted text-sm">
      <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading cross-module signals...
    </div>
  );

  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="cross-module-widgets">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Top Supplier Overcharges */}
        <Widget
          icon={TrendingDown}
          tone="error"
          title="Top Supplier Overcharges"
          testid="widget-supplier-overcharges"
        >
          {data.top_supplier_overcharges.length === 0 ? (
            <p className="text-xs text-text-muted">No overcharges detected ✓</p>
          ) : data.top_supplier_overcharges.map(s => (
            <div key={s.supplier_name} className="text-xs flex justify-between py-1 border-b border-border last:border-0">
              <span className="truncate pr-2">{s.supplier_name}</span>
              <span className="font-semibold text-state-error">+₹{s.total_overpaid.toLocaleString()}</span>
            </div>
          ))}
        </Widget>

        {/* Top Margin Items */}
        <Widget
          icon={Star}
          tone="success"
          title="Top Margin Items"
          testid="widget-top-margin"
        >
          {data.top_margin_items.length === 0 ? (
            <p className="text-xs text-text-muted">No menu data yet</p>
          ) : data.top_margin_items.map(it => (
            <div key={it.name} className="text-xs flex justify-between py-1 border-b border-border last:border-0">
              <div className="truncate pr-2">
                <span className="font-medium">{it.name}</span>
                <span className="text-text-muted ml-1">({it.classification})</span>
              </div>
              <span className="font-semibold text-state-success">{(it.margin_percentage || 0).toFixed(0)}%</span>
            </div>
          ))}
        </Widget>

        {/* Bank vs POS Gap */}
        <Widget
          icon={Banknote}
          tone={Math.abs(data.bank_vs_pos_gap.total_variance) > 100 ? 'warning' : 'info'}
          title="Bank vs POS Gap"
          testid="widget-bank-gap"
        >
          <Row label="Expected" value={`₹${(data.bank_vs_pos_gap.total_expected || 0).toLocaleString()}`} />
          <Row label="Actual" value={`₹${(data.bank_vs_pos_gap.total_actual || 0).toLocaleString()}`} />
          <Row
            label="Variance"
            value={`₹${(data.bank_vs_pos_gap.total_variance || 0).toLocaleString()}`}
            color={data.bank_vs_pos_gap.total_variance < 0 ? 'text-state-error' : 'text-state-success'}
          />
          <Row label="Unmatched" value={data.bank_vs_pos_gap.unmatched_count} />
        </Widget>

        {/* Pending Imports + File Counts */}
        <Widget
          icon={ClipboardList}
          tone={data.pending_imports.total > 0 ? 'warning' : 'success'}
          title="AI Review Queue"
          testid="widget-pending"
        >
          {data.pending_imports.total === 0 ? (
            <p className="text-xs text-text-muted">No pending reviews ✓</p>
          ) : (
            <>
              <p className="text-2xl font-bold text-state-warning mb-1">{data.pending_imports.total}</p>
              <p className="text-xs text-text-muted mb-2">rows awaiting confirmation</p>
              {Object.entries(data.pending_imports.by_module).map(([m, n]) => (
                <Row key={m} label={m} value={n} />
              ))}
            </>
          )}
        </Widget>
      </div>

      {/* Files-by-module + Unified Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-border rounded-md p-4" data-testid="widget-files-by-module">
          <div className="flex items-center space-x-2 mb-3">
            <FolderOpen className="h-4 w-4 text-primary" />
            <h4 className="font-semibold text-primary text-sm">Files by Module</h4>
          </div>
          {Object.keys(data.files_by_module).length === 0 ? (
            <p className="text-xs text-text-muted">No files uploaded yet</p>
          ) : Object.entries(data.files_by_module).map(([m, n]) => (
            <Row key={m} label={m} value={n} />
          ))}
        </div>

        <div className="bg-white border border-border rounded-md p-5 lg:col-span-2" data-testid="widget-unified-insights">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Sparkles className="h-4 w-4 text-accent" />
              <h4 className="font-semibold text-primary text-sm">Cross-Module AI Insights</h4>
            </div>
            <button
              onClick={() => loadUnified(unified != null)}
              disabled={unifiedLoading}
              className="text-xs font-semibold text-primary hover:underline disabled:opacity-50"
              data-testid="generate-unified-insights"
              title={unified != null ? 'Force a fresh AI regeneration (counts against your rate limit)' : ''}
            >
              {unifiedLoading ? 'Analysing...' : (unified ? 'Regenerate' : 'Generate Insights')}
            </button>
          </div>
          {unified?.cache?.hit && !unifiedLoading && (
            <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2" data-testid="unified-insights-cache-hit">
              served from cache · expires in ~{Math.max(0, Math.round((unified.cache.expires_in_seconds || 0) / 60))} min
            </div>
          )}
          {unifiedLoading ? (
            <div className="text-center py-6 text-text-muted text-sm">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
              GenAI is connecting dots across all your modules...
            </div>
          ) : unified?.ai_insights ? (
            <p className="text-sm text-text-secondary whitespace-pre-wrap leading-relaxed">{unified.ai_insights}</p>
          ) : (
            <p className="text-sm text-text-muted">
              Click "Generate Insights" to let AI summarise the top 3 priorities across
              suppliers, recon, menu, and uploaded files.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const TONE_BORDERS = {
  success: 'border-state-success/40',
  error: 'border-state-error/40',
  warning: 'border-state-warning/40',
  info: 'border-state-info/40',
};
const TONE_ICONS = {
  success: 'text-state-success',
  error: 'text-state-error',
  warning: 'text-state-warning',
  info: 'text-state-info',
};

const Widget = ({ icon: Icon, tone, title, children, testid }) => (
  <div className={`bg-white border ${TONE_BORDERS[tone] || 'border-border'} rounded-md p-4`} data-testid={testid}>
    <div className="flex items-center space-x-2 mb-3">
      <Icon className={`h-4 w-4 ${TONE_ICONS[tone] || 'text-primary'}`} />
      <h4 className="font-semibold text-primary text-xs uppercase tracking-wider">{title}</h4>
    </div>
    <div className="space-y-0.5">{children}</div>
  </div>
);

const Row = ({ label, value, color = 'text-text-primary' }) => (
  <div className="text-xs flex justify-between py-0.5">
    <span className="capitalize text-text-muted">{label}</span>
    <span className={`font-semibold ${color}`}>{value}</span>
  </div>
);

export default CrossModuleWidgets;
